import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Grid,
} from "@mui/material";
import { get } from "src/utils/networkUtils";
import UsersCountSummary from "src/components/users/UsersCountSummary";
import ProgressBarSummary from "src/components/users/ProgressBarSummary";
import { fCoins } from "src/utils/formatNumber";
import PacksByTypeTable from "src/components/store-item/PacksByTypeTable";
import DailyPacksTable from "src/components/store-item/DailyPacksTable";
import WelcomePacksTable from "src/components/store-item/WelcomePacksTable";
import AllAccessPacksTable from "src/components/store-item/AllAccessPacksTable";
import useWithUrlTenant from "src/hooks/useTenant";

export default function PacksNewDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [packsStats, setPacksStats] = useState(null);
  const [packsStatsLoading, setPacksStatsLoading] = useState(true);
  const [storePackTypes, setStorePackTypes] = useState([]);
  const [dailyPackTypes, setDailyPackTypes] = useState([]);
  const [welcomePackTypes, setWelcomePackTypes] = useState([]);
  const [allAccessPackTypes, setAllAccessPackTypes] = useState([]);
  const withTenant = useWithUrlTenant();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      get(withTenant(`/admin/marketplace/pack-items-by-types`))
        .then((response) => {
          if (response.success) {
            setStorePackTypes(response.storePackTypes || []);
            setDailyPackTypes(response.dailyPackTypes || []);
            setWelcomePackTypes(response.welcomePackTypes || []);
            setAllAccessPackTypes(response.allAccessPackTypes || []);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error("Error loading pack types:", error);
          setLoading(false);
        });
      setPacksStatsLoading(true);
      get(withTenant(`/admin/marketplace/pack/stats`))
        .then((response) => {
          setPacksStats(response);
          setPacksStatsLoading(false);
        })
        .catch((error) => {
          console.error("Error loading packs stats:", error);
          setPacksStatsLoading(false);
        });
    };

    loadData();
  }, [withTenant]);

  return (
    <>
      <Helmet>
        <title> Packs </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h4" gutterBottom>
            Packs
          </Typography>
        </Stack>

        {packsStatsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Store Pack Opens"
                    total={packsStats?.openStorePacksCount ?? 0}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Avg Store Pack Cost"
                    total={`${
                      packsStats?.packCoinsSpent &&
                      packsStats?.openStorePacksCount
                        ? Number(
                            (
                              packsStats.packCoinsSpent /
                              packsStats.openStorePacksCount
                            ).toFixed(2)
                          )
                        : 0
                    }`}
                    icon="/assets/icons/ufcoin.svg"
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Total Store Pack Revenue"
                    total={`${
                      packsStats?.packCoinsSpent
                        ? fCoins(packsStats?.packCoinsSpent)
                        : 0
                    }`}
                    icon="/assets/icons/ufcoin.svg"
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Avg Store Pack Open/User"
                    total={`${
                      packsStats?.totalStorePackUsersCount
                        ? (
                            packsStats.openStorePacksCount /
                            packsStats.totalStorePackUsersCount
                          ).toFixed(2)
                        : "0.00"
                    }`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="Daily Pack Opens"
                    total={`${packsStats?.totalOpenDailyPacks ?? 0}`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="Avg Daily Pack Open/User"
                    total={`${
                      packsStats?.openDailyPackUsersCount
                        ? Math.round(
                            packsStats?.totalOpenDailyPacks /
                              packsStats?.openDailyPackUsersCount
                          ) ?? 0
                        : 0
                    }`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={4}>
              {packsStatsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 200,
                    border: 1,
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : packsStats.topPackTypes ? (
                <ProgressBarSummary
                  title=""
                  total="Top 5 Packs"
                  sx={{ mb: 2 }}
                  data={(() => {
                    const maxItems = Math.max(
                      ...packsStats.topPackTypes.map(
                        (item) => item.purchaseCount
                      )
                    );
                    return packsStats.topPackTypes.map((item) => ({
                      title: item.name,
                      amount: item.count,
                      pct: Math.round((item.count / maxItems) * 80),
                      store: 0,
                    }));
                  })()}
                />
              ) : (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    border: 1,
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Failed to load store items data
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        )}

        {!loading && storePackTypes.length > 0 && (
          <PacksByTypeTable rows={storePackTypes} />
        )}
        {!loading && dailyPackTypes.length > 0 && (
          <DailyPacksTable rows={dailyPackTypes} />
        )}

        {!loading && welcomePackTypes.length > 0 && (
          <WelcomePacksTable rows={welcomePackTypes} />
        )}

        {!loading && allAccessPackTypes.length > 0 && (
          <AllAccessPacksTable rows={allAccessPackTypes} />
        )}
      </Container>
    </>
  );
}
